import { skipToken } from '@reduxjs/toolkit/query'
import { useEffect, useMemo, useRef } from 'react'

import { useWindow } from '@krp/client-runtime'
import {
  NEW_PRODUCT,
  ONEC_WINDOWS,
  ProductCreateScreen,
  draftDiscarded,
  draftFieldChanged,
  draftSimilarChecked,
  makeSelectProductCreateVm,
  onecProductSearch,
  operationDismissed,
  productOperationResendRequested,
  productSaveRequested,
  type ProductCreateActions,
  normalizeRid,
} from '@krp/resource-onec-ui'

import { useAppDispatch, useAppSelector } from '../../app/hooks'
import type { RootState } from '../../app/types'

/** Сколько похожих товаров просим у поиска при проверке названия. */
const SIMILAR_PRODUCTS_LIMIT = 10
const EMPTY_BASE = { name: '', article: '', barcode: '' }

/** Binding окна «Новый товар 1С»: база — из адреса окна, черновик — `<rid>|new`. */
export function OneCProductCreateBinding() {
  const window = useWindow()
  const dispatch = useAppDispatch()
  // rid — в одном написании: ключи кэша, теги и черновики не расходятся по регистру GUID.
  const resourceId = normalizeRid(window.params.rid ?? '')
  const checkedName = useAppSelector(
    (state) => state.onec.products.drafts[`${resourceId}|${NEW_PRODUCT}`]?.checkedName ?? null,
  )
  const similarArgs = useMemo(
    () => (checkedName === null ? skipToken : { resourceId, q: checkedName, limit: SIMILAR_PRODUCTS_LIMIT }),
    [checkedName, resourceId],
  )
  onecProductSearch.useQuerySubscription(similarArgs)

  const selectVm = useMemo(() => {
    const selectPackageVm = makeSelectProductCreateVm()
    const selectSimilar = onecProductSearch.select(similarArgs)
    return (state: RootState) =>
      selectPackageVm({
        resourceId,
        intent: state.onec.products,
        operations: state.onec.productOperations,
        similar: selectSimilar(state),
      })
  }, [resourceId, similarArgs])
  const vm = useAppSelector(selectVm)
  // Последнее наименование — в ref: actions не пересоздаются на каждый символ.
  const name = useRef(vm.form.name.value)
  useEffect(() => {
    name.current = vm.form.name.value
  }, [vm.form.name.value])
  const { replaceWith, close } = window

  const actions = useMemo<ProductCreateActions>(
    () => ({
      fieldChanged: (field, value) =>
        dispatch(
          draftFieldChanged({
            resourceId,
            target: NEW_PRODUCT,
            field,
            value,
            base: EMPTY_BASE,
            baseVersion: null,
            now: new Date().toISOString(),
          }),
        ),
      checkSimilar: () => dispatch(draftSimilarChecked({ resourceId, name: name.current, now: new Date().toISOString() })),
      create: () => dispatch(productSaveRequested({ resourceId, target: NEW_PRODUCT })),
      discard: () => dispatch(draftDiscarded({ resourceId, target: NEW_PRODUCT })),
      resend: (key) => dispatch(productOperationResendRequested({ key })),
      dismiss: (key) => dispatch(operationDismissed({ key })),
      open: (refKey) => replaceWith(ONEC_WINDOWS.product, { rid: resourceId, key: refKey }),
      close,
    }),
    [close, dispatch, replaceWith, resourceId],
  )

  return <ProductCreateScreen vm={vm} actions={actions} />
}
